import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GranularityOptions, LensGranularity, PreviousGranularityOptions } from "../models";
import { INSIGHTS_TIME_RANGE_PARAM } from "../utils/insightsNavigation";

export interface TimeRangeChangeParams {
    selectedTimeRange: LensGranularity;
    searchParams: URLSearchParams;
}

export type OnTimeRangeChange = (params: TimeRangeChangeParams) => void;

interface UseTimeRangeParams {
    defaultRange?: LensGranularity;
    onTimeRangeChange?: OnTimeRangeChange;
}

const VALID_VALUES = new Set<string>(Object.values(LensGranularity));

const isLensGranularity = (value: string): value is LensGranularity => VALID_VALUES.has(value);

const parseTimeRange = (search: string, fallback: LensGranularity): LensGranularity => {
    const value = new URLSearchParams(search).get(INSIGHTS_TIME_RANGE_PARAM);
    return value && isLensGranularity(value) ? value : fallback;
};

export const useTimeRange = ({ defaultRange = LensGranularity.Month, onTimeRangeChange }: UseTimeRangeParams = {}): {
    selectedTimeRange: LensGranularity;
    setSelectedTimeRange: (value: LensGranularity) => void;
    isAllTimeSelected: boolean;
    selectedLastTimeRangeLabel: string;
    selectedPreviousTimeRangeLabel: string | null;
} => {
    const { search } = useLocation();
    const navigate = useNavigate();

    const selectedTimeRange = useMemo(() => parseTimeRange(search, defaultRange), [search, defaultRange]);

    const handleDefaultTimeRangeChange = useCallback(
        ({ searchParams }: TimeRangeChangeParams) => {
            navigate({ search: searchParams.toString() });
        },
        [navigate],
    );
    const handleTimeRangeChange = onTimeRangeChange ?? handleDefaultTimeRangeChange;

    const setSelectedTimeRange = useCallback(
        (value: LensGranularity) => {
            const params = new URLSearchParams(search);
            params.set(INSIGHTS_TIME_RANGE_PARAM, value);
            handleTimeRangeChange({ selectedTimeRange: value, searchParams: params });
        },
        [handleTimeRangeChange, search],
    );

    const isAllTimeSelected = selectedTimeRange === LensGranularity.Lifetime;

    const selectedLastTimeRangeLabel = useMemo(
        () => GranularityOptions.find(({ value }) => value === selectedTimeRange)?.label ?? "-",
        [selectedTimeRange],
    );

    const selectedPreviousTimeRangeLabel = useMemo(() => {
        if (selectedTimeRange === LensGranularity.Lifetime) {
            return null;
        }
        return PreviousGranularityOptions.find(({ value }) => value === selectedTimeRange)?.label ?? null;
    }, [selectedTimeRange]);

    return {
        selectedTimeRange,
        setSelectedTimeRange,
        isAllTimeSelected,
        selectedLastTimeRangeLabel,
        selectedPreviousTimeRangeLabel,
    };
};
